
import { cancelBetCL } from '@/api'
import { useStore } from '@/store'
import { AppMutation } from '@/store/types'
import { BetType, EventBuss } from '@/types/global'
import { abbreviateNumber } from '@/utils'
import {
    ChipOption,
    computed,
    defineComponent,
    Emitter,
    inject,
    onBeforeUnmount,
    onMounted,
    PropType,
    ref,
    StyleValue,
    TableData
} from 'vue'
import { useI18n } from 'vue-i18n'

export default defineComponent({
    emits: ['place-bet', 'cancel-bet', 'on-error'],
    props: {
        label: {
            required: false,
            default: '',
            type: String as PropType<string>
        },
        ratio: {
            required: false,
            default: '',
            type: String as PropType<string>
        },
        color: {
            type: Number as PropType<number>,
            required: true
        },
        confirmedBet: {
            type: Number as PropType<number>,
            required: false,
            default: 0
        },
        tempBet: {
            type: Number as PropType<number>,
            required: false,
            default: 0
        },
        totalBet: {
            type: Number as PropType<number>,
            required: false,
            default: 0
        },
        betType: {
            type: Number as PropType<number>,
            required: true
        },
        width: {
            type: Number as PropType<number>,
            required: true
        },
        height: {
            type: Number as PropType<number>,
            required: true
        },
        desk: {
            type: Object as PropType<TableData>,
            required: false
        },
        isFreeBaccarat: {
            type: Boolean as PropType<boolean>,
            required: false,
            default: false
        },
        isGrayMode: {
            type: Boolean as PropType<boolean>,
            default: false
        },
        allowBet: {
            type: Boolean as PropType<boolean>,
            default: false
        },
        errorMessage: {
            type: String as PropType<string>,
            required: false,
            default: ''
        },
        gameType: {
            type: Number as PropType<number>,
            required: false,
            default: -1
        },
        showFlashingBg: {
            type: Boolean as PropType<boolean>,
            default: false
        }
    },
    setup(props, { emit }) {
        const emitter = inject('emitter') as Emitter
        const store = useStore()
        const { t } = useI18n()
        const chipsUI = ref<
            {
                x: number
                y: number
                amount: number
                class: string
                type: 'temp' | 'confirmed'
            }[]
        >([])

        onMounted(() => {
            emitter.on(EventBuss.PLACE_CHIP, handleOnPlaceChip)
        })

        onBeforeUnmount(() => {
            emitter.off(EventBuss.PLACE_CHIP, handleOnPlaceChip)
        })

        const betColor = computed(() => {
            const { color } = props

            let result = ''
            if (color === 1) result = 'red'
            else if (color === 2) result = 'green'
            else if (color === 3) result = 'blue'
            else if (color === 4) result = 'super6'

            if (props.isGrayMode) return 'gray'
            return result
        })

        const sumOfTempBetandConfirmedBet = computed(() => {
            const confirmedBet = props.confirmedBet ?? 0
            const tempBet = props.tempBet ?? 0
            return confirmedBet + tempBet
        })

        const toShortValue = (value: number) => {
            return abbreviateNumber(value)
        }

        const placeBet = () => {
            if (props.isGrayMode && props.allowBet) {
                let error = props.errorMessage

                if (!error) {
                    if (props.betType === BetType.DRAGON_7) {
                        error = t('prompts.d7error')
                    } else if (props.betType === BetType.BEAR_8) {
                        error = t('prompts.b8error')
                    } else if (props.betType === BetType.BIG_TIGER) {
                        error = t('prompts.bigtigererror')
                    } else if (props.betType === BetType.SMALL_TIGER) {
                        error = t('prompts.smalltigererror')
                    } else if (props.betType === BetType.SUPER6) {
                        error = t('prompts.super6tigermutual')
                    }
                }

                if (error) emit('on-error', error)
                return
            }

            if (!props.allowBet && props.isGrayMode) {
                return
            }

            emit('place-bet', props.betType)
        }

        const cancelBet = () => {
            // console.log('cancel Bet', props.betType)

            if (props.desk) {
                const params = {
                    memberId: memberId.value,
                    tableNo: props.desk.tableNo,
                    gameNo: props.desk.expand.gameNo,
                    playNum: '',
                    token: token.value
                }

                const betType = props.betType
                if (betType === BetType.BANKER && props.isFreeBaccarat) {
                    params.playNum = '1-20-201'
                } else if (betType === BetType.BANKER && !props.isFreeBaccarat) {
                    params.playNum = '1-14-141'
                } else if (betType === BetType.PLAYER) params.playNum = '1-15-151'
                else if (betType === BetType.BC_TIE) params.playNum = '1-16-161'
                else if (betType === BetType.PLAYER_PAIR)
                    params.playNum = '1-15-152'
                else if (betType === BetType.BANKER_PAIR)
                    params.playNum = '1-14-142'
                else if (betType === BetType.DRAGON) params.playNum = '1-17-171'
                else if (betType === BetType.TIGER) params.playNum = '1-18-181'
                else if (betType === BetType.DT_TIE) params.playNum = '1-19-191'

                else if (betType === BetType.DRAGON_7) params.playNum = '1-22-221'
                else if (betType === BetType.BEAR_8) params.playNum = '1-23-231'
                else if (betType === BetType.BIG_TIGER) params.playNum = '1-24-241'
                else if (betType === BetType.SMALL_TIGER) params.playNum = '1-25-251'
                else if (betType === BetType.SUPER6) params.playNum = '1-21-211'

                // super6  1-21-211
                // 龙七 | dragon7  1-22-221
                // 熊八 | bear8  1-23-231
                // 大老虎 | big tiger  1-24-241
                // 小老虎 | small tiger  1-25-251


                store.commit(AppMutation.SHOW_LOADING)
                cancelBetCL(params)
                    .then((response) => {
                        store.commit(AppMutation.HIDE_LOADING)

                        if (response.data.status === 200) {
                            emit('cancel-bet', props.betType)
                            resetBetUI()
                        } else if (response.data.status === 100) {
                            emitter.emit(EventBuss.SMALL_TOAST, {
                                msg: response.data.description,
                                id: props.desk?.id || 0
                            })
                        }
                    })
                    .catch(() => {
                        store.commit(AppMutation.HIDE_LOADING)

                        emitter.emit(EventBuss.SMALL_TOAST, {
                            msg: t('prompts.networkerror'),
                            id: props.desk?.id || 0
                        })
                    })
            }
        }

        const removeTempUIBet = () => {
            chipsUI.value = chipsUI.value.filter((i) => i.type !== 'temp')
        }

        const applyTempUIBet = () => {
            chipsUI.value = chipsUI.value.map((i) => {
                if (i.type === 'temp') i.type = 'confirmed'
                return i
            })

            // console.log(chipsUI.value)
        }

        const handleOnPlaceChip = (arg: {
            t: number
            amt: number
            type: 'temp' | 'confirmed'
            deskId: number
        }) => {
            if (props.desk && props.desk.id !== arg.deskId) return

            if (arg.t !== props.betType) return

            if (arg.type === 'confirmed') {
                const sum = chipsUI.value.reduce((s, c) => s + c.amount, 0)
                if (chipsUI.value.length > 0 && sum === arg.amt) {
                    // Keep existing chip pieces (e.g. after applyTempUIBet)
                    chipsUI.value = chipsUI.value.map((i) => ({
                        ...i,
                        type: 'confirmed' as const
                    }))
                    return
                }
                chipsUI.value = []
            }

            // Same dimensions as chipContainerStyle (landscape only)
            const w = Math.abs(props.width)
            const h = Math.abs(props.height)

            const chipSize = 16
            const chipHalf = chipSize / 2

            // Keep chip fully inside (x/y are centers due to translate(-50%, -50%))
            const minX = chipHalf
            const maxX = Math.max(chipHalf, w - chipHalf)
            const minY = chipHalf
            const maxY = Math.max(chipHalf, h - chipHalf)

            let startX = Math.floor(w / 2)
            let startY = Math.floor(h / 2)

            if (chipsUI.value.length > 0) {
                const lastItem = chipsUI.value[chipsUI.value.length - 1]
                startX = lastItem.x
                startY = lastItem.y
            }

            const clamp = (v: number, min: number, max: number) =>
                Math.min(Math.max(v, min), max)

            // Prefer near previous chip, but never leave the box
            let _x = getRandomPosition(minX, maxX)
            let tries = 0
            while (Math.abs(_x - startX) > chipSize && tries < 20) {
                _x = clamp(getRandomPosition(minX, maxX), minX, maxX)
                tries++
            }
            _x = clamp(_x, minX, maxX)

            let _y = getRandomPosition(minY, maxY)
            tries = 0
            while (Math.abs(_y - startY) > chipSize && tries < 20) {
                _y = clamp(getRandomPosition(minY, maxY), minY, maxY)
                tries++
            }
            _y = clamp(_y, minY, maxY)

            let _chipIndex = chips.value.value.indexOf(arg.amt)

            if (_chipIndex === -1) _chipIndex = 22

            chipsUI.value.push({
                x: _x,
                y: _y,
                amount: arg.amt,
                class: `_chip_${_chipIndex}`,
                type: arg.type
            })
        }

        const resetBetUI = () => {
            chipsUI.value = []
        }

        const getChipPositionStyle = (post: number): string => {
            return `calc(var(--pc1)*${post})`
        }

        const getRandomPosition = (min: number, max: number) => {
            return Math.floor(Math.random() * (max - min + 1) + min)
        }

        const showCancelBet = computed((): boolean => {
            if (
                props.desk &&
                userCommandType.value === 2 &&
                props.desk.status === 1 &&
                props.confirmedBet > 0
            ) {
                return true
            }
            return false
        })

        const chipContainerStyle = computed((): StyleValue | undefined => {
            if (props.width > 0 && props.height > 0) {
                const w = Math.abs(props.width)
                const h = Math.abs(props.height)

                return {
                    width: `calc(var(--pc1)*${w})`,
                    height: `calc(var(--pc1)*${h})`,
                    // backgroundColor: 'red'
                }
            }
            return undefined
        })

        const chips = computed((): ChipOption => store.getters['chips'])
        const memberId = computed(() => store.getters['memberid'])
        const userCommandType = computed(() => store.getters['commandType'])
        const token = computed(() => store.getters['token'])
        const isSpecialPlayMode = computed(
            (): boolean =>
                props.gameType === 1 && store.getters['playMode'] === 2
        )

        return {
            chipContainerStyle,
            betColor,
            isSpecialPlayMode,
            sumOfTempBetandConfirmedBet,
            chipsUI,
            placeBet,
            toShortValue,
            handleOnPlaceChip,
            resetBetUI,
            removeTempUIBet,
            applyTempUIBet,
            cancelBet,
            showCancelBet,
            getChipPositionStyle
        }
    }
})
